<template>
  <component
    :is="tag"
    :style="overFlowWrapStyle"
    :class="{ text: true, [`vs-${type}`]: type, truncate, center, bold }"
  >
    <slot />
  </component>
</template>

<script setup lang="ts">
/**
 * Text is used to add a supporting text to your screen
 */
import { computed } from 'vue'

const props = withDefaults(
  defineProps<{
    /**
     * Changes the html tag
     */
    tag?: string
    /**
     * Changes the styling of the text to visually communicate its emphasis
     *
     * paragraph - Default text body
     * alt-paragraph - Paragraph with reduced contrast
     * subdued - Italicize and reduce contrast
     * caption - Make the text smaller than default
     * intro - Make the text bigger than default
     */
    type?: string
    truncate?: boolean
    center?: boolean
    bold?: boolean
    overFlowWrap?: string
    /**
     * Change the overflow wrap style
     *
     * normal - Default value. Sequences of white space are preserved. Lines are broken at newline characters, at <br>, and as necessary to fill line boxes.
     * break-word - To prevent overflow, an otherwise unbreakable string of characters — like a long word or URL — may be broken at any point if there are no otherwise-acceptable break points in the line.
     * anywhere - Sequences of white space are preserved. Lines are broken at newline characters, at <br>, and as necessary to fill line boxes.
     * See: https://developer.mozilla.org/en-US/docs/Web/CSS/overflow-wrap
     */
  }>(),
  {
    tag: 'p',
    type: 'paragraph',
    truncate: false,
    center: false,
    bold: false,
    overFlowWrap: 'break-word',
  },
)

const overFlowWrapStyle = computed(() => ({
  'overflow-wrap': props.overFlowWrap,
}))
</script>

<style scoped>
.text {
  margin: 0;
  font-size: 16px;
  line-height: 24px;
  font-family: var(--font-regular), serif;
  color: var(--color-grey--dark);
  white-space: pre-wrap;
}

.vs-alt-paragraph {
  color: var(--color-grey);
}

.vs-caption,
.vs-subdued {
  color: var(--color-grey);
  font-size: 14px;
  line-height: 20px;
}

.vs-subdued {
  font-style: italic;
}

.vs-warning {
  color: var(--color-red);
}

.vs-intro {
  font-size: 18px;
  line-height: 24px;
}

.vs-overline {
  font-size: 12px;
  line-height: 16px;
  text-transform: uppercase;
  color: var(--color-grey);
}

.truncate {
  max-width: 100%;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.center {
  text-align: center;
}

.bold {
  font-weight: bold;
}
</style>
